import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import AccountingShell from "./AccountingShell";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const INR = (n) =>
  `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const num = (n) => Number(n || 0);

const fmtDate = (v) => {
  try {
    const d = v?.toDate ? v.toDate() : v ? new Date(v) : null;
    if (!d || Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-IN");
  } catch {
    return "—";
  }
};

const getDateObj = (v) => {
  try {
    const d = v?.toDate ? v.toDate() : v ? new Date(v) : null;
    return d && !Number.isNaN(d.getTime()) ? d : null;
  } catch {
    return null;
  }
};

const getFY = (v) => {
  const d = getDateObj(v) || new Date();
  const y = d.getFullYear();
  const start = d.getMonth() + 1 >= 4 ? y : y - 1;
  return `${String(start).slice(-2)}${String(start + 1).slice(-2)}`;
};

const ledgerDate = (r) =>
  r.invoiceDate ||
  r.tenderFeeInvoiceDate ||
  r.registrationInvoiceDate ||
  r.paymentDate ||
  r.paymentReceiptDate ||
  r.tenderFeeReceiptDate ||
  r.registrationPaymentReceiptDate ||
  r.emdPaymentDate ||
  r.emdBgDate ||
  r.emdRefundDate ||
  r.raBillDate ||
  r.raDate ||
  r.createdAt;

const voucherName = (type = "") => {
  const t = String(type).toUpperCase();
  if (t === "REGISTRATION_GST_INVOICE") return "Registration GST Invoice";
  if (t === "REGISTRATION_PAYMENT_RECEIVED") return "Registration Payment";
  if (t === "TENDER_FEE_INVOICE") return "Tender Fee Invoice";
  if (t === "TENDER_FEE_PAYMENT_RECEIVED") return "Tender Fee Payment";
  if (t === "EMD_RECEIVED_HELD") return "EMD Held";
  if (t === "EMD_CONVERTED_TO_BG_SECURITY") return "EMD BG Security";
  if (t === "EMD_REFUND") return "EMD Refund";
  if (t.includes("RA_BILL")) return "RA Bill";
  if (t.includes("PAYMENT")) return "Payment";
  if (t.includes("REFUND")) return "Refund";
  if (t.includes("INVOICE")) return "Invoice";
  return type || "Voucher";
};

const isInvoice = (r) => String(r.type || "").toUpperCase().includes("INVOICE");
const isPayment = (r) =>
  String(r.type || "").toUpperCase().includes("PAYMENT") ||
  String(r.type || "").toUpperCase().includes("RECEIVED");
const isGST = (r) => isInvoice(r) && num(r.gstAmount) > 0;
const isEMD = (r) => String(r.type || "").toUpperCase().includes("EMD");
const isRA = (r) => String(r.type || "").toUpperCase().includes("RA_BILL");
const isRefund = (r) => String(r.type || "").toUpperCase().includes("REFUND");

const duplicateKey = (r) =>
  [
    r.vendorUid || r.vendorName || r.contractorName || "NA",
    r.tenderId || "NA",
    r.applicationId || r.raBillId || r.billId || "NA",
    r.type || "NA",
    r.voucherNo || r.invoiceNo || r.receiptNo || r.referenceNo || "NA",
  ].join("__");

const reportOptions = [
  { id: "DASHBOARD", label: "Dynamic Dashboard" },
  { id: "GST", label: "GST Report" },
  { id: "EMD", label: "EMD Register" },
  { id: "RA", label: "RA Bill Report" },
  { id: "BALANCE", label: "Contractor Balance" },
  { id: "PAYMENT", label: "Payment Register" },
  { id: "REFUND", label: "Refund Register" },
  { id: "DUPLICATE", label: "Duplicate Verification" },
  { id: "LEDGER", label: "Full Ledger" },
];

export default function AccountingReports() {
  return (
    <AccountingShell title="Accounting Reports">
      <ReportsContent />
    </AccountingShell>
  );
}

function ReportsContent() {
  const [ledger, setLedger] = useState([]);
  const [raBills, setRaBills] = useState([]);
  const [fy, setFy] = useState("ALL");
  const [report, setReport] = useState("DASHBOARD");
  const [search, setSearch] = useState("");

  const load = async () => {
    const [ledgerSnap, raSnap] = await Promise.all([
      getDocs(collection(db, "contractor_ledgers")),
      getDocs(collection(db, "ra_bills")).catch(() => ({ docs: [] })),
    ]);

    setLedger(ledgerSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setRaBills(raSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    load();
  }, []);

  const cleanLedger = useMemo(() => {
    const map = new Map();

    ledger.forEach((r) => {
      const key = duplicateKey(r);
      const old = map.get(key);

      if (!old) {
        map.set(key, { ...r, duplicateCount: 1 });
      } else {
        const oldTime = getDateObj(old.updatedAt || old.createdAt)?.getTime() || 0;
        const newTime = getDateObj(r.updatedAt || r.createdAt)?.getTime() || 0;
        map.set(key, {
          ...(newTime >= oldTime ? r : old),
          duplicateCount: num(old.duplicateCount || 1) + 1,
        });
      }
    });

    return Array.from(map.values());
  }, [ledger]);

  const fyOptions = useMemo(() => {
    const set = new Set();
    cleanLedger.forEach((r) => set.add(getFY(ledgerDate(r))));
    return ["ALL", ...Array.from(set).filter(Boolean).sort().reverse()];
  }, [cleanLedger]);

  const filteredLedger = useMemo(() => {
    let list = [...cleanLedger];

    if (fy !== "ALL") {
      list = list.filter((r) => getFY(ledgerDate(r)) === fy);
    }

    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((r) =>
        [
          r.vendorName,
          r.contractorName,
          r.vendorUid,
          r.tenderId,
          r.voucherNo,
          r.invoiceNo,
          r.referenceNo,
          r.type,
          r.narration,
          r.paymentUtr,
        ]
          .join(" ")
          .toLowerCase()
          .includes(s)
      );
    }

    return list.sort(
      (a, b) =>
        (getDateObj(ledgerDate(a))?.getTime() || 0) -
        (getDateObj(ledgerDate(b))?.getTime() || 0)
    );
  }, [cleanLedger, fy, search]);

  const gstRows = filteredLedger.filter(isGST);
  const emdRows = filteredLedger.filter(isEMD);
  const paymentRows = filteredLedger.filter(isPayment);
  const refundRows = filteredLedger.filter(isRefund);
  const duplicateRows = cleanLedger.filter((r) => num(r.duplicateCount) > 1);

  const raRows = useMemo(() => {
    const fromLedger = filteredLedger.filter(isRA);
    const direct = raBills.map((r) => ({
      id: r.id,
      type: "RA_BILL_DIRECT",
      vendorName: r.vendorName || r.contractorName || r.vendorUid || "—",
      tenderId: r.tenderId || "—",
      voucherNo: r.raBillNo || r.billNo || r.id,
      taxableAmount: num(r.taxableAmount),
      gstAmount: num(r.gstAmount),
      grossAmount: num(r.totalAmount || r.billAmount),
      debit: num(r.approvedAmount || r.billAmount || r.totalAmount),
      credit: num(r.paidAmount),
      status: r.status || r.paymentStatus || "—",
      createdAt: r.createdAt || r.raBillDate,
      narration: r.narration || "RA bill record",
    }));
    return [...fromLedger, ...direct];
  }, [filteredLedger, raBills]);

  const balanceRows = useMemo(() => {
    const map = new Map();

    filteredLedger.forEach((r) => {
      const key = r.vendorUid || r.vendorName || r.contractorName || "Unknown";
      const old = map.get(key) || {
        party: key,
        debit: 0,
        credit: 0,
        balance: 0,
        entries: 0,
      };

      old.debit += num(r.debit);
      old.credit += num(r.credit);
      old.balance = old.debit - old.credit;
      old.entries += 1;
      map.set(key, old);
    });

    return Array.from(map.values()).sort((a, b) => b.balance - a.balance);
  }, [filteredLedger]);

  const currentRows =
    report === "GST"
      ? gstRows
      : report === "EMD"
      ? emdRows
      : report === "RA"
      ? raRows
      : report === "BALANCE"
      ? balanceRows
      : report === "PAYMENT"
      ? paymentRows
      : report === "REFUND"
      ? refundRows
      : report === "DUPLICATE"
      ? duplicateRows
      : filteredLedger;

  const summary = useMemo(() => {
    const debit = filteredLedger.reduce((s, r) => s + num(r.debit), 0);
    const credit = filteredLedger.reduce((s, r) => s + num(r.credit), 0);
    const taxable = gstRows.reduce((s, r) => s + num(r.taxableAmount), 0);
    const gst = gstRows.reduce((s, r) => s + num(r.gstAmount), 0);
    const emd = emdRows.reduce((s, r) => s + num(r.credit || r.debit || r.grossAmount), 0);
    const ra = raRows.reduce((s, r) => s + num(r.debit || r.grossAmount), 0);
    const payment = paymentRows.reduce((s, r) => s + num(r.credit), 0);
    const refund = refundRows.reduce((s, r) => s + num(r.debit), 0);

    return {
      entries: filteredLedger.length,
      debit,
      credit,
      balance: debit - credit,
      taxable,
      gst,
      emd,
      ra,
      payment,
      refund,
      duplicate: duplicateRows.length,
    };
  }, [filteredLedger, gstRows, emdRows, raRows, paymentRows, refundRows, duplicateRows]);

  const rowsForExport = () => {
    if (report === "BALANCE") {
      return balanceRows.map((r, i) => ({
        Sr: i + 1,
        Party: r.party,
        Debit: r.debit,
        Credit: r.credit,
        Balance: r.balance,
        Entries: r.entries,
      }));
    }

    return currentRows.map((r, i) => ({
      Sr: i + 1,
      FY: getFY(ledgerDate(r)),
      Date: fmtDate(ledgerDate(r)),
      VoucherType: voucherName(r.type),
      VoucherNo: r.voucherNo || r.invoiceNo || r.referenceNo || r.id,
      Party: r.vendorName || r.contractorName || r.vendorUid || "—",
      Tender: r.tenderId || "—",
      Taxable: num(r.taxableAmount),
      GST: num(r.gstAmount),
      Gross: num(r.grossAmount || r.debit || r.credit),
      Debit: num(r.debit),
      Credit: num(r.credit),
      Status: r.status || "",
      Narration: r.narration || "",
      DuplicateCount: r.duplicateCount || 1,
    }));
  };

  const downloadExcel = () => {
    const wb = XLSX.utils.book_new();

    const summaryData = [
      ["Report", reportTitle(report)],
      ["Financial Year", fy],
      ["Entries", summary.entries],
      ["Debit", summary.debit],
      ["Credit", summary.credit],
      ["Balance", summary.balance],
      ["Taxable", summary.taxable],
      ["GST", summary.gst],
      ["EMD", summary.emd],
      ["RA Bill", summary.ra],
      ["Payments", summary.payment],
      ["Refunds", summary.refund],
      ["Duplicate Count", summary.duplicate],
    ];

    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryData), "Summary");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rowsForExport()), "Report Data");
    XLSX.writeFile(wb, `${reportTitle(report)}_${fy}.xlsx`);
  };

  const downloadPDF = () => {
    const pdf = new jsPDF("l", "mm", "a4");
    const pageW = pdf.internal.pageSize.getWidth();

    pdf.setFillColor(15, 23, 42);
    pdf.rect(0, 0, pageW, 20, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.text("Shine Agri Allied & Marketing Multistate Cooperative Society Ltd.", pageW / 2, 8, {
      align: "center",
    });
    pdf.setFontSize(9);
    pdf.text(`${reportTitle(report)} | ${fy === "ALL" ? "All FY" : `FY ${fy}`}`, pageW / 2, 15, {
      align: "center",
    });

    pdf.setTextColor(0, 0, 0);
    autoTable(pdf, {
      startY: 25,
      theme: "grid",
      head: [["Metric", "Value", "Metric", "Value"]],
      body: [
        ["Entries", summary.entries, "Balance", INR(summary.balance)],
        ["Debit", INR(summary.debit), "Credit", INR(summary.credit)],
        ["Taxable", INR(summary.taxable), "GST", INR(summary.gst)],
        ["EMD", INR(summary.emd), "RA Bill", INR(summary.ra)],
        ["Payments", INR(summary.payment), "Duplicates", summary.duplicate],
      ],
      headStyles: { fillColor: [15, 23, 42] },
      styles: { fontSize: 8 },
    });

    const data = rowsForExport().slice(0, 500);

    autoTable(pdf, {
      startY: pdf.lastAutoTable.finalY + 8,
      theme: "grid",
      head: [Object.keys(data[0] || { Notice: "No Records" })],
      body: data.length ? data.map((r) => Object.values(r)) : [["No records found"]],
      headStyles: { fillColor: [15, 23, 42] },
      styles: { fontSize: 6, cellPadding: 1.5 },
      margin: { left: 5, right: 5 },
    });

    pdf.save(`${reportTitle(report)}_${fy}.pdf`);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 xl:grid-cols-8 gap-3">
        <SummaryCard title="Entries" value={summary.entries} />
        <SummaryCard title="Debit" value={INR(summary.debit)} red />
        <SummaryCard title="Credit" value={INR(summary.credit)} green />
        <SummaryCard title="Balance" value={INR(summary.balance)} red={summary.balance > 0} green={summary.balance <= 0} />
        <SummaryCard title="GST" value={INR(summary.gst)} />
        <SummaryCard title="EMD" value={INR(summary.emd)} green />
        <SummaryCard title="Payments" value={INR(summary.payment)} green />
        <SummaryCard title="Duplicates" value={summary.duplicate} red={summary.duplicate > 0} />
      </div>

      <div className="bg-white rounded-2xl border p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <select className="input" value={report} onChange={(e) => setReport(e.target.value)}>
            {reportOptions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>

          <select className="input" value={fy} onChange={(e) => setFy(e.target.value)}>
            {fyOptions.map((f) => (
              <option key={f} value={f}>
                {f === "ALL" ? "All Financial Years" : `FY ${f}`}
              </option>
            ))}
          </select>

          <input
            className="input"
            placeholder="Search party / voucher / tender / UTR..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <button className="btn-dark" onClick={downloadPDF}>
            Download PDF
          </button>

          <button className="btn-green" onClick={downloadExcel}>
            Download Excel
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        {reportOptions.slice(1).map((r) => (
          <ReportCard
            key={r.id}
            title={r.label}
            active={report === r.id}
            onClick={() => setReport(r.id)}
          />
        ))}
      </div>

      <div className="bg-white rounded-2xl border p-4">
        <div className="mb-4 text-center">
          <h2 className="text-lg font-black">{reportTitle(report)}</h2>
          <p className="text-xs text-slate-500">
            FY-wise dynamic report with duplicate verification, PDF and Excel export
          </p>
        </div>

        {report === "BALANCE" ? (
          <BalanceTable rows={balanceRows} />
        ) : (
          <LedgerTable rows={currentRows} />
        )}
      </div>

      <style>{`
        .input{
          width:100%;
          border:1px solid #cbd5e1;
          border-radius:12px;
          padding:10px 12px;
          font-size:12px;
          font-weight:800;
        }
        .btn-dark{
          background:#0f172a;
          color:white;
          padding:10px 14px;
          border-radius:12px;
          font-size:12px;
          font-weight:900;
        }
        .btn-green{
          background:#059669;
          color:white;
          padding:10px 14px;
          border-radius:12px;
          font-size:12px;
          font-weight:900;
        }
      `}</style>
    </div>
  );
}

function reportTitle(report) {
  const found = reportOptions.find((r) => r.id === report);
  return found?.label || "Accounting Report";
}

function LedgerTable({ rows }) {
  return (
    <div className="overflow-auto">
      <table className="min-w-[1550px] w-full text-[11px] border border-slate-300">
        <thead className="bg-slate-900 text-white">
          <tr>
            <th className="p-2 border">Sr</th>
            <th className="p-2 border">FY</th>
            <th className="p-2 border">Date</th>
            <th className="p-2 border text-left">Voucher</th>
            <th className="p-2 border text-left">Party</th>
            <th className="p-2 border text-left">Tender</th>
            <th className="p-2 border text-right">Taxable</th>
            <th className="p-2 border text-right">GST</th>
            <th className="p-2 border text-right">Gross</th>
            <th className="p-2 border text-right">Debit</th>
            <th className="p-2 border text-right">Credit</th>
            <th className="p-2 border text-center">Duplicate</th>
            <th className="p-2 border text-left">Status / Narration</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className={num(r.duplicateCount) > 1 ? "bg-red-50" : "hover:bg-slate-50"}>
              <td className="p-2 border text-center">{i + 1}</td>
              <td className="p-2 border text-center font-black">FY {getFY(ledgerDate(r))}</td>
              <td className="p-2 border text-center">{fmtDate(ledgerDate(r))}</td>
              <td className="p-2 border font-black">
                {voucherName(r.type)}
                <div className="text-[10px] text-slate-500">
                  {r.voucherNo || r.invoiceNo || r.referenceNo || r.id}
                </div>
              </td>
              <td className="p-2 border font-bold">
                {r.vendorName || r.contractorName || r.vendorUid || "—"}
              </td>
              <td className="p-2 border">{r.tenderId || "—"}</td>
              <td className="p-2 border text-right">{num(r.taxableAmount) ? INR(r.taxableAmount) : "—"}</td>
              <td className="p-2 border text-right">{num(r.gstAmount) ? INR(r.gstAmount) : "—"}</td>
              <td className="p-2 border text-right">{num(r.grossAmount) ? INR(r.grossAmount) : "—"}</td>
              <td className="p-2 border text-right text-red-600 font-black">{num(r.debit) ? INR(r.debit) : "—"}</td>
              <td className="p-2 border text-right text-emerald-700 font-black">{num(r.credit) ? INR(r.credit) : "—"}</td>
              <td className="p-2 border text-center">
                {num(r.duplicateCount) > 1 ? (
                  <span className="bg-red-100 text-red-700 rounded-full px-3 py-1 font-black">
                    {r.duplicateCount} Found
                  </span>
                ) : (
                  <span className="bg-emerald-100 text-emerald-700 rounded-full px-3 py-1 font-black">
                    OK
                  </span>
                )}
              </td>
              <td className="p-2 border">
                <div className="font-bold">{r.status || "—"}</div>
                <div className="text-[10px] text-slate-500">{r.narration || "—"}</div>
              </td>
            </tr>
          ))}

          {!rows.length && (
            <tr>
              <td colSpan={13} className="p-8 text-center text-slate-500">
                No records found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function BalanceTable({ rows }) {
  return (
    <div className="overflow-auto">
      <table className="min-w-[900px] w-full text-[11px] border border-slate-300">
        <thead className="bg-slate-900 text-white">
          <tr>
            <th className="p-2 border">Sr</th>
            <th className="p-2 border text-left">Contractor / Party</th>
            <th className="p-2 border text-right">Debit</th>
            <th className="p-2 border text-right">Credit</th>
            <th className="p-2 border text-right">Balance</th>
            <th className="p-2 border text-center">Entries</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((r, i) => (
            <tr key={r.party}>
              <td className="p-2 border text-center">{i + 1}</td>
              <td className="p-2 border font-black">{r.party}</td>
              <td className="p-2 border text-right text-red-600 font-black">{INR(r.debit)}</td>
              <td className="p-2 border text-right text-emerald-700 font-black">{INR(r.credit)}</td>
              <td className="p-2 border text-right font-black">{INR(r.balance)}</td>
              <td className="p-2 border text-center">{r.entries}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReportCard({ title, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-2xl border p-5 shadow-sm ${
        active ? "bg-slate-900 text-white" : "bg-white text-slate-900"
      }`}
    >
      <h2 className="font-black text-sm">{title}</h2>
      <p className={`text-xs mt-2 ${active ? "text-slate-300" : "text-slate-500"}`}>
        Open dynamic report
      </p>
    </button>
  );
}

function SummaryCard({ title, value, green, red }) {
  return (
    <div className="bg-white rounded-2xl border p-4 shadow-sm">
      <p className="text-xs text-slate-500">{title}</p>
      <h2 className={`text-lg font-black mt-2 ${green ? "text-emerald-700" : red ? "text-red-600" : "text-slate-900"}`}>
        {value}
      </h2>
    </div>
  );
}